import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ExternalLink, FileText, Loader2, X } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { Checkbox } from "../ui/checkbox";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

// Portrait pop up over a greyed out page: the draft Statement of Work
// rendered page by page, with the agreement at the end of the document.
export function SowReviewModal({ open, onClose, previewUrl, company, onAgree }) {
  const bodyRef = useRef(null);
  const endRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [failed, setFailed] = useState(false);
  const [endVisible, setEndVisible] = useState(false);
  const file = useMemo(() => ({ url: previewUrl, withCredentials: true }), [previewUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const measure = useCallback((node) => {
    bodyRef.current = node;
    if (!node) return;
    const ro = new ResizeObserver(() => setPageWidth(Math.max(0, node.clientWidth - 48)));
    ro.observe(node);
    setPageWidth(Math.max(0, node.clientWidth - 48));
  }, []);

  useEffect(() => {
    if (!open || !numPages || !endRef.current) return undefined;
    const io = new IntersectionObserver(([e]) => setEndVisible(e.isIntersecting), { root: bodyRef.current, threshold: 0.4 });
    io.observe(endRef.current);
    return () => io.disconnect();
  }, [open, numPages]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-[#0B1220]/75 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Draft Statement of Work" data-testid="sow-review-modal" onClick={onClose}>
      <div className="relative flex flex-col w-[min(96vw,912px)] h-[min(96vh,1080px)] rounded-xl bg-stone-100 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-4 px-4 sm:px-5 h-14 shrink-0 bg-[#0B1220] text-white">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-[#3EA8DC] shrink-0" />
            <span className="font-medium truncate">Draft Statement of Work</span>
            {numPages > 0 && <span className="hidden sm:inline font-mono text-[11px] text-stone-400">{numPages} pages</span>}
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-stone-300 hover:text-white transition-colors" data-testid="sow-review-open-tab">
              <span className="hidden sm:inline">Open as a PDF</span><ExternalLink className="w-4 h-4" />
            </a>
            <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors" aria-label="Close" data-testid="sow-review-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div ref={measure} className="relative flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6" data-testid="sow-review-body">
          <Document
            file={file}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setFailed(false); }}
            onLoadError={() => setFailed(true)}
            loading={<div className="flex flex-col items-center justify-center gap-3 py-24 text-stone-500" data-testid="sow-review-loading"><Loader2 className="w-6 h-6 animate-spin text-[#1B4F8A]" /><span className="text-sm">Preparing your draft</span></div>}
            error={<div className="flex flex-col items-center gap-3 py-24 text-center text-stone-600" data-testid="sow-review-error"><p className="text-sm">We could not display the draft here.</p><a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#1B4F8A] px-5 py-2.5 text-sm font-semibold text-white">Open as a PDF <ExternalLink className="w-4 h-4" /></a></div>}
            className="flex flex-col items-center gap-5"
          >
            {pageWidth > 0 && Array.from({ length: numPages }, (_, i) => (
              <Page key={i} pageNumber={i + 1} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} className="shadow-[0_2px_12px_rgba(11,18,32,0.12)] bg-white" />
            ))}
          </Document>

          {numPages > 0 && !failed && (
            <div ref={endRef} className="mt-8 mb-2" data-testid="sow-review-end">
              <p className="mb-2 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-stone-500">End of document</p>
              <label className="flex items-start gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-4 text-[15px] leading-relaxed text-stone-800 cursor-pointer" data-testid="sow-review-agree-label">
                <Checkbox checked={false} onCheckedChange={(v) => v && onAgree()} className="mt-1 h-5 w-5 shrink-0 border-stone-400 data-[state=checked]:bg-[#1B4F8A] data-[state=checked]:border-[#1B4F8A]" data-testid="sow-review-agree" />
                <span>I have read the Statement of Work and the Medicys Terms and Conditions of Business and I agree to them. I am authorised to commission this work on behalf of {company}.</span>
              </label>
              <p className="mt-2 text-xs text-stone-500" data-testid="sow-review-hint">Once ticked, this window closes and the signature step opens.</p>
            </div>
          )}
        </div>

        {numPages > 0 && !failed && !endVisible && (
          <button type="button" onClick={() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })} className="absolute left-1/2 -translate-x-1/2 bottom-6 sm:bottom-9 inline-flex items-center gap-2 rounded-full bg-[#1B4F8A] px-4 py-2 text-xs font-semibold text-white shadow-lg hover:bg-[#25629f] transition-colors" data-testid="sow-review-scroll-hint">
            Read to the end to confirm <ArrowDown className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
